import React, { useState } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, Spinner } from '@material-tailwind/react';
import { BanknoteIcon, ChevronDownIcon, ChevronRightIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import { useGetInvoices } from '../../../custom-hooks/use-invoices';
import { formatNumberWithCommas, convertToPresentableText } from '../../../utils/functions';
import { INVOICE_STATUSES, PAYMENT_METHODS } from '../../../utils/enums';
import CreateInvoiceModal from '../../invoice/CreateInvoiceModal';

const statusColors = {
    draft: 'gray', delivered: 'blue', partially_paid: 'amber', paid: 'green', void: 'red',
};
const statusLabels = {
    draft: 'Draft', delivered: 'Delivered', partially_paid: 'Partially Paid', paid: 'Paid', void: 'Void',
};

const Payments = ({ customer }) => {
    const navigate = useNavigate();
    const [expandedId, setExpandedId] = useState(null);
    const [createInvoiceModal, setCreateInvoiceModal] = useState(false);

    const { data, isLoading, error, refetch } = useGetInvoices({ customer: customer._id, limit: 100 });
    const invoices = data?.invoices || [];

    const nonVoidInvoices = invoices.filter(inv => inv.status !== INVOICE_STATUSES.VOID);
    const totalInvoiced = nonVoidInvoices.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0);
    const totalPaid = nonVoidInvoices.reduce((sum, inv) => sum + (inv.paidAmount || 0), 0);
    const outstandingBalance = nonVoidInvoices.reduce((sum, inv) => sum + (inv.balanceAmount || 0), 0);

    const toggleExpand = (id) => {
        setExpandedId(prev => (prev === id ? null : id));
    };

    return (
        <div className="border rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <BanknoteIcon size={16} />
                    Payments
                </h2>
                <div className="flex gap-2">
                    <Button
                        className="rounded-full border flex items-center gap-2"
                        size="sm"
                        variant="outlined"
                        onClick={() => refetch()}
                    >
                        <RefreshCwIcon size={16} />
                        Refresh
                    </Button>
                    <Button
                        className="rounded-full border flex items-center gap-2"
                        size="sm"
                        variant="gradient"
                        onClick={() => setCreateInvoiceModal(true)}
                    >
                        <PlusIcon size={16} />
                        Create Invoice
                    </Button>
                </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-3">
                    <p className="text-sm text-blue-600">Total Invoiced</p>
                    <p className="text-2xl font-bold text-blue-800">PKR {formatNumberWithCommas(totalInvoiced)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-sm text-green-600">Total Paid</p>
                    <p className="text-2xl font-bold text-green-800">PKR {formatNumberWithCommas(totalPaid)}</p>
                </div>
                <div className={`rounded-lg p-3 ${outstandingBalance > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${outstandingBalance > 0 ? 'text-amber-600' : 'text-gray-600'}`}>Outstanding Balance</p>
                    <p className={`text-2xl font-bold ${outstandingBalance > 0 ? 'text-amber-800' : 'text-gray-800'}`}>
                        PKR {formatNumberWithCommas(outstandingBalance)}
                    </p>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center p-8">
                    <Spinner />
                </div>
            ) : error ? (
                <div className="text-center py-8 text-red-500">Error loading invoices</div>
            ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">No invoices found for {customer.name}</div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50">
                                <th className="py-2 px-3 w-8"></th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600">Invoice #</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600">Period</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600 text-right">Total</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600 text-right">Paid</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600 text-right">Balance</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600">Status</th>
                                <th className="py-2 px-3 text-xs font-semibold text-gray-600">Due Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices.map((inv) => (
                                <React.Fragment key={inv._id}>
                                    <tr
                                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                        onClick={() => toggleExpand(inv._id)}
                                    >
                                        <td className="py-2 px-3 text-gray-400">
                                            {expandedId === inv._id ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                                        </td>
                                        <td
                                            className="py-2 px-3 text-sm font-medium text-blue-600 hover:underline"
                                            onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${inv._id}`); }}
                                        >
                                            {inv.invoiceNumber}
                                        </td>
                                        <td className="py-2 px-3 text-sm">
                                            {moment({ month: inv.billingPeriod?.month - 1, year: inv.billingPeriod?.year }).format('MMM YYYY')}
                                        </td>
                                        <td className="py-2 px-3 text-sm text-right font-medium">PKR {formatNumberWithCommas(inv.totalAmount)}</td>
                                        <td className="py-2 px-3 text-sm text-right text-green-600">PKR {formatNumberWithCommas(inv.paidAmount)}</td>
                                        <td className={`py-2 px-3 text-sm text-right ${inv.balanceAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                            PKR {formatNumberWithCommas(inv.balanceAmount)}
                                        </td>
                                        <td className="py-2 px-3">
                                            <Chip
                                                value={statusLabels[inv.status] || inv.status}
                                                color={statusColors[inv.status] || 'gray'}
                                                size="sm"
                                                variant="ghost"
                                                className="text-[10px] uppercase font-bold rounded-full text-center"
                                            />
                                        </td>
                                        <td className="py-2 px-3 text-sm">{inv.dueDate ? moment(inv.dueDate).format('MMM DD') : '-'}</td>
                                    </tr>
                                    {expandedId === inv._id && (
                                        <tr className="bg-gray-50">
                                            <td colSpan={8} className="px-4 py-3">
                                                {(!inv.payments || inv.payments.length === 0) ? (
                                                    <div className="text-xs text-gray-400 py-2">No payments recorded yet.</div>
                                                ) : (
                                                    <table className="w-full text-left">
                                                        <thead>
                                                            <tr>
                                                                <th className="py-1 px-2 text-[10px] font-semibold text-gray-500 uppercase">Date</th>
                                                                <th className="py-1 px-2 text-[10px] font-semibold text-gray-500 uppercase text-right">Amount</th>
                                                                <th className="py-1 px-2 text-[10px] font-semibold text-gray-500 uppercase">Method</th>
                                                                <th className="py-1 px-2 text-[10px] font-semibold text-gray-500 uppercase">Recorded By</th>
                                                                <th className="py-1 px-2 text-[10px] font-semibold text-gray-500 uppercase">Note</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {inv.payments.map((p, idx) => (
                                                                <tr key={idx} className="border-t border-gray-200">
                                                                    <td className="py-1.5 px-2 text-xs">{moment(p.paymentDate).format('MMM DD, YYYY')}</td>
                                                                    <td className="py-1.5 px-2 text-xs text-right font-medium">PKR {formatNumberWithCommas(p.amount)}</td>
                                                                    <td className="py-1.5 px-2 text-xs">
                                                                        {p.paymentMethod === PAYMENT_METHODS.SECURITY_ADJUSTMENT
                                                                            ? 'Security Adj.'
                                                                            : convertToPresentableText(p.paymentMethod || '-')}
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-xs">
                                                                        {p.markedByCustomer ? 'Customer' : (p.receivedBy?.name || 'Admin')}
                                                                    </td>
                                                                    <td className="py-1.5 px-2 text-xs text-gray-500">{p.note || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CreateInvoiceModal
                isOpen={createInvoiceModal}
                onClose={() => setCreateInvoiceModal(false)}
                presetCustomer={customer}
                onSuccess={() => refetch()}
            />
        </div>
    );
};

export default Payments;
